/**
 * Puente con la extensión (SDD §6.5): detección y handoff de sesión por
 * `externally_connectable`. Chrome y Edge asignan IDs distintos, así que se
 * prueban en orden con timeout.
 */
import { CONFIG } from './config'

export interface ExtensionInfo {
  id: string
  version: string
  connected: boolean
  /** ¿Está fijada en la barra? null si el navegador no lo informa. */
  pinned: boolean | null
}

function send<T>(id: string, message: unknown, timeoutMs = 1000): Promise<T | null> {
  return new Promise((resolve) => {
    const rt = typeof chrome !== 'undefined' ? chrome?.runtime : undefined
    if (!rt?.sendMessage) return resolve(null)
    const timer = setTimeout(() => resolve(null), timeoutMs)
    try {
      rt.sendMessage(id, message, (res) => {
        clearTimeout(timer)
        // lastError: la extensión con ese ID no está instalada
        resolve(rt.lastError ? null : ((res as T) ?? null))
      })
    } catch {
      clearTimeout(timer)
      resolve(null)
    }
  })
}

/** ¿Hay una extensión de eco instalada? Devuelve la primera que responde. */
export async function detectExtension(ids = CONFIG.extensionIds): Promise<ExtensionInfo | null> {
  for (const id of ids) {
    const r = await send<{
      ok: boolean
      version: string
      conectada?: boolean
      enBarra?: boolean | null
    }>(id, { type: 'ping' })
    if (r?.ok)
      return { id, version: r.version, connected: Boolean(r.conectada), pinned: r.enBarra ?? null }
  }
  return null
}

/** Entrega a la extensión un token de un solo uso para que abra su propia sesión. */
export async function connectExtension(extensionId: string, hashedToken: string): Promise<boolean> {
  const r = await send<{ ok: boolean }>(extensionId, { type: 'session:set', hashedToken }, 8000)
  return Boolean(r?.ok)
}

/** Prueba de la bienvenida: graba solo el micrófono durante `segundos`. */
export async function startTestRecording(extensionId: string, segundos: number): Promise<boolean> {
  const r = await send<{ ok: boolean }>(extensionId, { type: 'test:start', segundos }, 15_000)
  return Boolean(r?.ok)
}

export async function disconnectExtension(extensionId: string): Promise<void> {
  await send(extensionId, { type: 'session:clear' })
}

export function browserName(): 'edge' | 'chrome' | 'otro' {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return 'edge'
  if (/Chrome\//.test(ua)) return 'chrome'
  return 'otro'
}
